import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { HandCoins, Bank, DeviceMobile, ArrowCircleDown, ArrowCircleUp } from "@phosphor-icons/react";

export default function PayoutsPage() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ upi_id: "", bank_account: "", ifsc: "", holder_name: "" });
  const [amt, setAmt] = useState(0);

  const refresh = () => api.get("/payouts/mine").then(({ data }) => {
    setData(data);
    setForm({
      upi_id: data.upi_id || "", bank_account: data.bank_account || "",
      ifsc: data.ifsc || "", holder_name: data.holder_name || "",
    });
  });

  useEffect(() => { refresh(); }, []);

  const saveDetails = async () => {
    try { await api.put("/payouts/details", form); toast.success("Details saved"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const requestPayout = async () => {
    if (!amt || amt <= 0) { toast.error("Enter amount"); return; }
    try {
      await api.post("/payouts/request", { amount: Number(amt) });
      toast.success("Payout requested — processing within 2 business days");
      setAmt(0); refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  if (!data) return <div className="min-h-[40vh] flex items-center justify-center"><div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
      <p className="text-xs tracking-[0.3em] uppercase text-gold mb-3">Owner earnings</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-midnight mb-10">Payouts</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="p-6 bg-midnight text-bone rounded-2xl">
          <p className="text-xs tracking-widest uppercase text-gold mb-2">Available balance</p>
          <p className="font-serif text-4xl" data-testid="payout-balance">₹{(data.balance || 0).toLocaleString("en-IN")}</p>
        </div>
        <div className="p-6 bg-white border border-black/5 rounded-2xl">
          <p className="text-xs tracking-widest uppercase text-neutral-500 mb-2">Lifetime earnings</p>
          <p className="font-serif text-4xl text-midnight">₹{(data.lifetime_earnings || 0).toLocaleString("en-IN")}</p>
        </div>
        <div className="p-6 bg-gold/10 rounded-2xl">
          <p className="text-xs tracking-widest uppercase text-gold mb-2">Transactions</p>
          <p className="font-serif text-4xl text-midnight">{(data.transactions || []).length}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-6 bg-white border border-black/5 rounded-2xl">
          <h3 className="font-serif text-xl text-midnight mb-4 flex items-center gap-2"><Bank size={20} weight="duotone" className="text-gold" /> Payout Details</h3>
          <div className="space-y-3">
            <div><Label>UPI ID</Label><Input value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} placeholder="yourname@upi" data-testid="upi-payout" /></div>
            <div><Label>Account holder name</Label><Input value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} /></div>
            <div><Label>Bank account number</Label><Input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} data-testid="bank-payout" /></div>
            <div><Label>IFSC code</Label><Input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} /></div>
            <Button onClick={saveDetails} className="w-full bg-midnight text-bone hover:bg-midnight-hover" data-testid="save-payout-details">Save details</Button>
          </div>
        </div>

        <div className="p-6 bg-white border border-black/5 rounded-2xl">
          <h3 className="font-serif text-xl text-midnight mb-4 flex items-center gap-2"><HandCoins size={20} weight="duotone" className="text-gold" /> Request Payout</h3>
          <p className="text-sm text-neutral-600 mb-4">Withdraw your available balance to your UPI or bank account. Typically processed within 2 business days.</p>
          <div className="space-y-3">
            <div><Label>Amount (₹)</Label><Input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} max={data.balance} data-testid="payout-amount" /></div>
            <Button onClick={requestPayout} disabled={!amt || amt > data.balance} className="w-full bg-gold text-midnight hover:bg-gold-hover" data-testid="request-payout-btn">
              Request Payout
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <h3 className="font-serif text-xl text-midnight mb-4">Transaction History</h3>
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
          {(data.transactions || []).length === 0 ? (
            <p className="p-12 text-center text-neutral-500">No transactions yet</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone">
                <tr className="text-left">
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">When</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Source</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500">Type</th>
                  <th className="p-4 text-xs tracking-widest uppercase text-neutral-500 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {[...(data.transactions || [])].reverse().map((t, i) => (
                  <tr key={i} className="border-t border-black/5">
                    <td className="p-4 text-xs text-neutral-500">{new Date(t.at).toLocaleString("en-IN")}</td>
                    <td className="p-4">{t.source}</td>
                    <td className="p-4">
                      {t.kind === "credit" ? (
                        <span className="flex items-center gap-1 text-emerald-700"><ArrowCircleDown size={14} weight="fill" /> Credit</span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-700"><ArrowCircleUp size={14} weight="fill" /> Debit</span>
                      )}
                    </td>
                    <td className="p-4 text-right font-medium">₹{t.amount.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
